import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUserOrgCase } from '@context/UserOrgCaseContext';
import { decodeSearchParams } from '@pages/CaseDashboard/utils';
import { FiltersForm } from '@shared/types/forms';
import { getTodayDate } from '@utils/dates';

interface UseFiltersInitialDates {
  defaultStartDate: string;
  defaultEndDate: string;
  startDate: string;
  endDate: string;
  isDefaultDateRange: boolean;
}

export const useFiltersInitialDates = (): UseFiltersInitialDates => {
  const [searchParams] = useSearchParams();

  const filters: FiltersForm = decodeSearchParams(searchParams);

  const { selectedCase } = useUserOrgCase();
  const today = useMemo(() => getTodayDate(), []);

  const submissionEndDate = useMemo(
    () =>
      selectedCase && selectedCase?.claimSubmissionEndDate < today
        ? selectedCase?.claimSubmissionEndDate
        : today,
    [selectedCase, today],
  );

  const startDate = useMemo(
    () => filters.startDate || selectedCase?.claimSubmissionStartDate,
    [selectedCase?.claimSubmissionStartDate, filters.startDate],
  );

  const endDate = useMemo(
    () => filters.endDate || submissionEndDate || today,
    [filters.endDate, submissionEndDate, today],
  );

  const isDefaultDateRange = useMemo(
    () =>
      !filters.startDate &&
      !filters.endDate &&
      filters.startDate === selectedCase?.claimSubmissionStartDate &&
      filters.endDate === submissionEndDate,
    [
      filters.startDate,
      filters.endDate,
      selectedCase?.claimSubmissionStartDate,
      submissionEndDate,
    ],
  );

  return {
    defaultStartDate: selectedCase?.claimSubmissionStartDate,
    defaultEndDate: submissionEndDate,
    startDate,
    endDate,
    isDefaultDateRange,
  };
};
